(ns hai.ui.components.collection.views.cards
  (:require
   [hai.sharing.helper :as sharing]
   [hai.ui.components :as c]
   [hai.ui.components.collection.form :as col.form]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.icons :as icons]
   [hai.ui.components.collection.views.utils :as view.utils]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.pagination :as pagination]
   [hai.ui.components.form.core :as form]
   [hai.ui.rf-sugar :refer [<s >e]]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(o/defstyled controls :div
  :flex :flex-row :gap-3 :items-center)

(o/defstyled filter-bar :div
  :flex :flex-row :gap-3 :items-center :flex-wrap)

(o/defstyled pagination-controls :div
  :flex :px-3 :py-2
  ;; `.pagination` is the inner row of controls (per-page, direction,
  ;; prev/next, total). On narrow viewports the row overflows the
  ;; container, so allow it to wrap and let the controls flow.
  [:.pagination :flex :flex-wrap :items-center :gap-3 :ml-auto
   ;; `gap-y-2` adds vertical spacing once a row wraps; px-only gap looks
   ;; cramped when the controls stack.
   {:row-gap "8px"}])

(o/defstyled loading-state :div
  :flex :justify-center :p-5
  ([]
   [:em "Loading..."]))

(o/defstyled error-state :div
  :flex :justify-center :p-5
  ([]
   [c/Text {:color "red"}
    [c/Code "An error occurred while loading"]]))

(o/defstyled no-items-state :div
  :flex :justify-center :p-5)

(defn pagination-footer
  "Pagination controls rendered as a plain div instead of a `<tr>`."
  [collection]
  (r/with-let [pagination (<s [:table/pagination collection])
               paginate?  (<s [:table/paginate? collection])]
    (let [{:table/keys [next-cursor prev-cursor total direction per-page]} @pagination]
      (when @paginate?
        [pagination-controls
         [:div.pagination
          [:span "Per page: "]
          [c/SelectRoot
           {:value         (str (or per-page pagination/default-per-page))
            :onValueChange (fn [value]
                             (let [per-page (js/parseInt value)]
                               (>e [:table/per-page collection per-page])))}
           [c/SelectTrigger {:variant "soft"}]
           [c/SelectContent
            [c/SelectItem {:value "5"} 5]
            [c/SelectItem {:value "10"} 10]
            [c/SelectItem {:value "25"} 25]
            [c/SelectItem {:value "50"} 50]]]
          [:span "Direction: "]
          [c/SelectRoot
           {:value         (name (or direction :asc))
            :onValueChange (fn [value]
                             (>e [:table/direction collection (keyword value)]))}
           [c/SelectTrigger {:variant "soft"}]
           [c/SelectContent
            [c/SelectItem {:value "asc"} "Asc"]
            [c/SelectItem {:value "desc"} "Desc"]]]
          [c/Button
           {:disabled (not prev-cursor)
            :on-click (fn [_] (>e [:table/prev-page collection]))
            :variant "soft"}
           "< Prev"]
          [c/Button
           {:disabled (not next-cursor)
            :on-click (fn [_] (>e [:table/next-page collection]))
            :variant "soft"}
           "Next >"]
          [:span "Total: " total]]]))))
;; ---------------------------------------------------------------------------
;; Rendering helpers
;; ---------------------------------------------------------------------------

(defn filter-fields
  "Renders filter fields horizontally in the controls area."
  [{:as collection
    :table/keys [bulk-actions columns]
    {{:table/keys [filters]} :form/fields} :table/form}]
  (when filters
    [form/component filters]))

(defn render-cell-value
  "Resolves the display value for a column given a row."
  [collection row {:table.column/keys [attr render render-with-collection]}]
  (cond
    render-with-collection [render-with-collection collection row]
    render                 [render row]
    attr                   (get row attr)
    :else                  [c/Text {:color "red"} [c/Code "No :attr or :render"]]))

(o/defstyled card-checkbox-wrap :span
  :inline-flex
  ;; The card surface is warm-ivory; Radix Checkbox's default thin grey border
  ;; nearly disappears. Wrap a darker, slightly thicker ring around it.
  [:button {:box-shadow "0 0 0 1px var(--card-forest-70)"
            :border-radius "4px"}])

(defn card-select-checkbox
  "Checkbox for bulk action selection, positioned in the top-right corner of a card."
  [collection {:form/keys [initial-value] :as _row}]
  ;; Both subs operate on the entity (the `:table/selected` collection
  ;; stores entities; bulk-action predicates expect entity shape too).
  ;; The table view passes `initial-value` for the same reason; do the
  ;; same here so the checkbox actually appears when the predicate looks
  ;; at fields like `:user.entity/permissions`.
  [:span
   ;; Cards that wrap us typically have their own on-click (navigate to
   ;; detail, etc.). Stop the checkbox click here so toggling selection
   ;; doesn't double up as a card-body click.
   {:on-click #(.stopPropagation %)}
   (when (bulk-actions/show? collection)
     [card-checkbox-wrap
      [bulk-actions/select-checkbox* collection initial-value]])
   ;; TODO removed for now as the card itself can be clicked and we only have edit views so there
   ;; is just one possible route
   #_[c/link-button
      ;; TODO fix
      {} #_{:on-click (fn [_] (>e [:routing/navigate #_route]))}
      [icons/outlined-edit]]])

(defn render-badges
  "Renders badge pills from a :table.card/badges column or a :table.card/badges-fn on the collection."
  [collection row]
  (when-let [badges-fn (:table.card/badges-fn collection)]
    (let [items (badges-fn row)]
      (when (seq items)
        [card/badges
         (for [label items]
           ^{:key label}
           [card/badge label])]))))

(defn render-footer
  "Renders the card footer from optional :table.card/tool and :table.card/meta columns."
  [collection row]
  (let [tool-fn  (:table.card/tool-fn collection)
        meta-fn  (:table.card/meta-fn collection)]
    (when (or tool-fn meta-fn)
      [card/solution-footer
       (when tool-fn
         [card/tool-indicator
          [:span.dot]
          (tool-fn row)])
       (when meta-fn
         [card/meta-info
          (meta-fn row)])])))

(defn render-cell
  "A component to render an individual cell with either a custom `:render` component or simply the `:attr`"
  [table row {:table.column/keys [attr render render-with-collection] :as _column}]
  (cond
    render-with-collection [render-with-collection table row]
    render [render row]
    attr [:div (get row attr)]
    :else [:div [c/Text {:color "red"} [c/Code "No `:attr` or `:render` on column definition"]]]))

(defn default-card-render
  "Default card body: renders badges, column-based title/description/fields, and footer."
  [{:as table
    :table/keys [columns]
    collection-fields :table/fields
    render-card :collection.view.card/render
    collection-editable? :collection/editable?}
   {:form/keys [fields current-value initial-value dirty? valid?] :as row}]
  [render-card row table]

  #_(let [class (cond-> []
                  dirty? (conj "dirty")
                  valid? (conj "valid")
                  (not valid?) (conj "invalid"))]
      (cond-> [:div {:class class}]
        (bulk-actions/show? table) (conj [bulk-actions/select-checkbox table initial-value])
        :always (into (map (fn [{:table.column/keys [attr field header] :as column}]
                             (when (string? header)
                               (if (and field attr (or collection-editable? (sharing/can-edit? current-value)))
                                 [field
                                  (cond-> (get fields attr)
                                    @(<s [:table/selected? table current-value])
                                    (assoc :table/selected? true)

                                    :always
                                    (assoc :collection/entity current-value))
                                  row]
                                 [render-cell
                                  table
                                  (cond-> current-value
                                    @(<s [:table/selected? table current-value])
                                    (assoc :table/selected? true))
                                  column]))))
                      columns)))

  #_(let [title-col       (first (filter #(= :title (:table.column/card-slot %)) columns))
          description-col (first (filter #(= :description (:table.column/card-slot %)) columns))
          field-cols      (remove #(#{:title :description} (:table.column/card-slot %)) columns)]
      [:<>
       [render-badges collection row]
       (when  title-col
         [card/solution-title (render-cell-value collection row title-col)])
       #_(when description-col
           [card/solution-description (render-cell-value collection row description-col)])
       #_(when (seq field-cols)
           (into [card/fields-layout]
                 (map (fn [column]
                        ^{:key (or (:table.column/id column) (:table.column/header column))}
                        [card/field
                         [:span.label (:table.column/header column)]
                         [:div.value (render-cell-value collection row column)]]))
                 field-cols))
       #_[render-footer collection row]]))

;; ---------------------------------------------------------------------------
;; Status indicators
;; ---------------------------------------------------------------------------

(defn loading-indicator
  [collection]
  (when @(<s [:table/resolving? collection])
    [loading-state]))

(defn error-indicator
  [collection]
  (when @(<s [:collection.query/error collection])
    [error-state]))

(defn no-items-indicator
  [collection rows]
  (when (and (empty? rows)
             (not @(<s [:table/resolving? collection]))
             (not @(<s [:collection.query/error collection])))
    [no-items-state
     (if-let [no-items (:table/no-items collection)]
       [no-items collection]
       [c/Text [:em "No items found"]])]))

;; TODO needs attention, duplicated in utils
(defn render-field
  "A component to render an individual cell with either a custom `:render` component or simply the `:attr`"
  [table row {:table.column/keys [attr render render-with-collection] :as _column}]
  (cond
    render-with-collection [render-with-collection table row]
    render [render row]
    attr [:div (get row attr)]
    :else [:div [c/Text {:color "red"} [c/Code "No `:attr` or `:render` on column definition"]]]))

(defn field
  [table item field]
  [view.utils/render-cell-or-field render-field table item field])

(defn column-render
  [{:table/keys [columns] :as table}
   {:form/keys [initial-value] :as row}]
  [card/fields-layout
   (when (seq columns)
     (into [card/fields-layout]
           (map (fn [{:table.column/keys [header render] :as column}]
                  [card/field
                   [:span.label header]
                   [view.utils/render-cell-or-field render-field table row column]]))
           columns))])

(o/defstyled card-render :div
  "Renders a single solution card. Uses :table/card-render for full custom rendering,
   otherwise falls back to default-card-render."
  ;; TODO this is a hack
  [col.form/link-input* :gap-0
   [icons/outlined-edit :hidden]]
  ([{:collection.view.card/keys [render] :as collection} row]
   (if render
     [render collection row]
     [default-card-render collection row])))

(defn view
  [{:table/keys [pagination-position]
    {{:table/keys [rows]} :form/fields} :table/form :as collection}]
  (let [current-rows (:form.field/current-value rows)
        card-render (partial card-render collection)
        rows (assoc rows
                    :form/render card-render
                    :form.field.collection/component card/solution-grid)
        ;; nil-safe: absent position == :bottom (prior behavior).
        position (or pagination-position :bottom)
        top? (#{:top :both} position)
        bottom? (#{:bottom :both} position)]
    [:<>
     ;; Gated behind the "Show filters" setting, whether the row is the built-in
     ;; one or a custom `:filters/render` bar.
     (when (-> collection :table/form :form/current-value :table/settings :show-filters-card?)
       [filters/cards-component collection])
     (when top?
       [pagination-footer collection])
     [form/collection rows]
     [no-items-indicator collection current-rows]
     [loading-indicator collection]
     [error-indicator collection]
     (when bottom?
       [pagination-footer collection])]))
